import { Link } from 'react-router-dom';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_budget-buddy-4768/artifacts/uj88omcr_ChatGPT%20Image%20Feb%2011%2C%202026%2C%2009_23_14%20PM.png';
const GUAPO_CHARACTER = 'https://customer-assets.emergentagent.com/job_gen-z-budgeting/artifacts/oab8ybbw_file_0000000021ac71f8b5eeb2af9d8946a7.png';

// Street Cred ranks data
const STREET_CRED_RANKS = [
  { days: 7, title: 'PAPER CHASER', emoji: '📄', desc: "You're just getting started" },
  { days: 30, title: 'BAG GETTER', emoji: '💰', desc: 'Securing the bag consistently' },
  { days: 90, title: 'HEAVY HITTER', emoji: '🔥', desc: 'Real discipline now' },
  { days: 365, title: 'MOGUL STATUS', emoji: '👑', desc: 'You run this' },
];

// Features data
const FEATURES = [
  { 
    icon: '🎯', 
    title: 'Debt Snowball Calculator', 
    desc: 'Crush your debt with avalanche or snowball strategies. Mark debts as PAID and watch your progress!'
  },
  { 
    icon: '💎', 
    title: 'Envelope Challenge', 
    desc: 'Gamified savings that hit different. Stack random amounts daily and build your savings muscle.'
  },
  { 
    icon: '📊', 
    title: 'Smart Dashboard', 
    desc: 'See your entire financial picture. Track income, bills, expenses, and leftover money at a glance.'
  },
  { 
    icon: '💬', 
    title: 'Guapo AI Assistant', 
    desc: 'Your personal finance advisor that speaks your language. Ask anything about budgeting!'
  },
  { 
    icon: '🔥', 
    title: 'Street Cred System', 
    desc: 'Build your streak, earn titles, and unlock rewards. From Paper Chaser to Mogul Status!'
  },
  { 
    icon: '🏠', 
    title: 'Household Sharing', 
    desc: 'Share your budget with family members. Everyone stays on the same page financially.'
  },
];

// How to close out a month
const MONTH_CLOSE_STEPS = [
  { step: 1, title: 'Review Your Dashboard', desc: 'Check all your bills are marked as paid and expenses are logged.' },
  { step: 2, title: 'Go to Income', desc: 'Navigate to the Income page from the main menu.' },
  { step: 3, title: 'Click "Wrap Up & Start Fresh"', desc: 'This button appears once you have income logged. It saves your month to History.' },
  { step: 4, title: 'View in History', desc: 'Your saved month appears in the History page. Bills reset to unpaid, and you start fresh!' },
];

function About() {
  return (
    <div className="max-w-4xl mx-auto space-y-8 pb-12">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-2xl p-8 text-white">
        <div className="flex items-center gap-4 mb-4">
          <img src={LOGO_URL} alt="GuapGoals" className="h-16 w-auto" />
          <div>
            <h1 className="text-3xl font-bold">About GuapGoals</h1>
            <p className="text-emerald-100">Get Your Money Right</p>
          </div>
        </div>
        <p className="text-lg text-emerald-50">
          GuapGoals is the budgeting app built for the real ones. Track your income, crush your debt, 
          build your savings, and level up your financial game.
        </p>
      </div>

      {/* Guapo Introduction */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col md:flex-row items-center gap-6">
          <img src={GUAPO_CHARACTER} alt="Guapo" className="w-32 h-32 object-cover rounded-full" />
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Meet Guapo</h2>
            <p className="text-gray-600">
              Your AI-powered budget buddy! Guapo is here to help you navigate GuapGoals, answer your 
              financial questions, and keep you motivated on your wealth-building journey. Just click 
              the chat button in the bottom right corner to talk to Guapo anytime.
            </p>
          </div>
        </div>
      </div>

      {/* Features Grid */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Features</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {FEATURES.map((feature) => (
            <div key={feature.title} className="p-4 bg-gray-50 rounded-xl">
              <div className="text-3xl mb-2">{feature.icon}</div>
              <h3 className="font-bold text-gray-900 mb-1">{feature.title}</h3>
              <p className="text-sm text-gray-600">{feature.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Street Cred System */}
      <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-xl p-6 text-white">
        <h2 className="text-2xl font-bold mb-2">🔥 The Street Cred System</h2>
        <p className="text-gray-300 mb-6">
          Build your streak by logging your finances daily. The longer you stay consistent, the higher 
          your status rises!
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {STREET_CRED_RANKS.map((rank) => (
            <div key={rank.title} className="bg-gray-800/50 rounded-lg p-4 text-center">
              <div className="text-4xl mb-2">{rank.emoji}</div>
              <div className="font-bold text-sm text-yellow-400">{rank.title}</div>
              <div className="text-xs text-gray-400 mt-1">{rank.days} days</div>
            </div>
          ))}
        </div>
        <div className="mt-6 p-4 bg-gray-800/50 rounded-lg">
          <h3 className="font-bold text-yellow-400 mb-2">🔄 Bounce Back Feature</h3>
          <p className="text-sm text-gray-300">
            At 14 days, you earn a "Bounce Back" - a streak freeze that lets you miss one day without 
            losing your streak. Life happens, and we've got your back!
          </p>
        </div>
      </div>

      {/* How to Close Out a Month */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-testid="month-close-guide">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">📅 How to Close Out a Month</h2>
        <p className="text-gray-600 mb-6">
          At the end of each month, close it out to archive your data and start fresh. Here's how:
        </p>
        <div className="space-y-4">
          {MONTH_CLOSE_STEPS.map((step) => (
            <div key={step.step} className="flex gap-4">
              <div className="w-10 h-10 bg-emerald-100 text-emerald-700 rounded-full flex items-center justify-center font-bold flex-shrink-0">
                {step.step}
              </div>
              <div>
                <h4 className="font-bold text-gray-900">{step.title}</h4>
                <p className="text-sm text-gray-600">{step.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* How History Works */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-testid="history-guide">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">📜 How History Works</h2>
        <p className="text-gray-600 mb-4">
          The History page lets you look back at your financial journey:
        </p>
        <ul className="space-y-3">
          <li className="flex items-start gap-3">
            <span className="text-emerald-500 mt-1">✓</span>
            <div>
              <strong className="text-gray-900">View Past Months</strong>
              <p className="text-sm text-gray-600">See income, bills, and expenses from previous months.</p>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <span className="text-emerald-500 mt-1">✓</span>
            <div>
              <strong className="text-gray-900">Track Your Progress</strong>
              <p className="text-sm text-gray-600">Compare spending patterns across different periods.</p>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <span className="text-emerald-500 mt-1">✓</span>
            <div>
              <strong className="text-gray-900">Bills Reset Automatically</strong>
              <p className="text-sm text-gray-600">Your paid status on bills resets each month so you can track fresh.</p>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <span className="text-emerald-500 mt-1">✓</span>
            <div>
              <strong className="text-gray-900">Keep Your Streak Going</strong>
              <p className="text-sm text-gray-600">Closing months doesn't affect your streak - keep building!</p>
            </div>
          </li>
        </ul>
      </div>

      {/* Quick Tips */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
        <h2 className="text-2xl font-bold text-yellow-800 mb-4">💡 Pro Tips</h2>
        <ul className="space-y-2 text-yellow-900">
          <li>• <strong>Mark bills as paid</strong> on the Dashboard to see green checkmarks</li>
          <li>• <strong>Log expenses daily</strong> to maintain your streak</li>
          <li>• <strong>Use the "No Spend Today" button</strong> when you don't spend anything</li>
          <li>• <strong>Check the Action Engine</strong> to see what to do with leftover money</li>
          <li>• <strong>Ask Guapo</strong> if you're ever confused - he's here to help!</li>
        </ul>
      </div>

      {/* Back to Dashboard */}
      <div className="text-center">
        <Link 
          to="/app/dashboard" 
          className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-600 text-white rounded-xl font-medium hover:bg-emerald-700 transition-colors"
        >
          ← Back to Dashboard
        </Link>
      </div>
    </div>
  );
}

export default About;
